/**
 * Los 8 prompts de ChatGPT Voice.
 *
 * En inglés a propósito: son contenido de entrenamiento, no interfaz
 * (SPEC.md §18). El Día 7 no tiene prompt.
 *
 * Reglas que estos textos deben respetar (CURRICULUM.md §4 y §10):
 *  - General American natural.
 *  - No corregir durante el primer intento de fluidez.
 *  - Máximo tres correcciones de alto impacto, y la de pronunciación solo si
 *    puede evaluarse con confianza.
 *  - Repetir solo una parte, y pasar inmediatamente a una situación distinta.
 *  - Nunca comportarse como profesor durante la performance.
 *  - Separar observaciones confiables de juicios de audio inciertos.
 */

import type { Area } from '../../domain/modelo/areas.js';
import type { BloqueEnfasis, ClavePrompt, PlantillaPrompt } from '../tipos.js';

/**
 * Nombre de cada área EN INGLÉS, para inyectar en los prompts.
 *
 * No confundir con `NOMBRE_AREA` de `domain/modelo/areas.ts`, que es el nombre
 * en español que ve el usuario en la interfaz.
 */
export const AREA_EN_INGLES: Record<Area, string> = {
  listening: 'listening comprehension',
  recuperacion_lexica: 'lexical retrieval (finding words fast)',
  habla_espontanea: 'spontaneous speaking',
  naturalidad: 'naturalness and word choice',
  gramatica_fluidez: 'grammar and fluency',
  pronunciacion_prosodia: 'pronunciation and prosody',
  interaccion_turnos: 'interaction and turn-taking',
};

export const PROMPTS: Record<ClavePrompt, PlantillaPrompt> = {
  baseline: {
    clave: 'baseline',
    base: `This is my English baseline. Do not teach me or correct me during the performance. Use natural General American English and keep the tasks spontaneous. Start with a short unprepared conversation, then make me tell a past story, explain something, give and defend an opinion, and handle an unexpected role-play. Challenge me with follow-up questions and make me reformulate at least once. At the end, give me a concise baseline summary covering fluency, listening, grammar, lexical retrieval, naturalness, interaction, and any pronunciation/prosody issues you can reliably notice. Clearly separate confident observations from uncertain audio judgments.`,
  },

  d1: {
    clave: 'd1',
    base: `Speak to me in natural General American English. Do not correct me during my first attempt. Start with an unexpected everyday topic. Ask follow-up questions, disagree with me once, and make me clarify or reformulate something. After the first task, give me only the three highest-impact corrections: one pronunciation/prosody issue if you can reliably hear one, one naturalness/word-choice issue, and one grammar/fluency issue. Make me redo only part of the task using the corrections, then immediately move me to a different situation that requires the same language functions. Keep the performance section English-only.`,
  },

  d2: {
    clave: 'd2',
    base: `Today focus on listening and pronunciation transfer. Use natural General American English. After I practice a short sound, reduction, stress, linking, rhythm, or intonation pattern, make me use it in NEW sentences and then in a short spontaneous conversation. Give immediate feedback during a narrow pronunciation drill, but do not interrupt constantly once we enter spontaneous speech.`,
  },

  d3: {
    clave: 'd3',
    base: `Run an unpredictable American-English conversation. Change topics without warning, interrupt naturally sometimes, misunderstand me once, ask me to rephrase, challenge one opinion, and give me incomplete information that I have to clarify. Do not behave like a language teacher during the first run. Afterward, select only the most useful corrections, make me repair them, and then switch to a new context.`,
  },

  d4: {
    clave: 'd4',
    base: `Test my active retrieval. Do not show me target expressions first. Give me short situations that require language from my previous errors and useful expressions. If I cannot retrieve something, let me circumlocute in English before helping. Then use the recovered language in an unscripted conversation and move it into a different context so it does not become a memorized script.`,
  },

  /**
   * CAMBIO PEDAGÓGICO 3 (aprobado).
   *
   * v2_2 decía: «Start by asking me which was hardest: listening, lexical
   * retrieval, ...». Eso contradice la spec maestra, que exige que la
   * APLICACIÓN calcule la sugerencia y la explique, y que el usuario la acepte
   * o la cambie. Preguntarlo otra vez en ChatGPT duplicaba la decisión y hacía
   * inútil la puntuación transparente del Día 5.
   *
   * Ahora el prompt RECIBE el foco ya decidido.
   */
  d5: {
    clave: 'd5',
    requiereFoco: true,
    base: `Today we adapt the session to one specific weak area that I have already identified for this week. Do not ask me to choose it: I am telling you what it is below. Spend most of the session working on that area, but finish with spontaneous transfer to a new real-world context. Avoid turning the practice into repetitive drills, and do not over-correct me while I am producing.`,
  },

  d6: {
    clave: 'd6',
    base: `If I cannot get a real human conversation today, act as a fallback only. Make the interaction socially realistic, unpredictable, and focused on listening and turn-taking rather than teaching. Do not over-correct me. At the end, ask what felt harder than talking to an AI.`,
  },

  checkpoint: {
    clave: 'checkpoint',
    base: `This is a progress checkpoint, not a normal lesson. Give me new, unprepared tasks comparable in difficulty to my baseline: spontaneous conversation, a past story, an explanation, an opinion I must defend, and an unexpected role-play. Include a short listening challenge. Do not coach me during the performance. At the end, compare only against my previous recorded observations if I provide them, and evaluate fluency, pauses, lexical retrieval, listening, naturalness, interaction, comprehensibility and pronunciation/prosody cautiously. Clearly separate confident observations from uncertain audio judgments.`,
  },
};

export interface OpcionesPrompt {
  /** Bloque de énfasis de la semana en curso. Añade una línea de contexto. */
  enfasis?: BloqueEnfasis | null;
  /** Foco del Día 5, ya decidido por la aplicación y aceptado por el usuario. */
  foco?: Area | null;
  /** Targets activos del Learning Bank que conviene mencionar. */
  targets?: string[];
}

/**
 * Construye el prompt final.
 *
 * Composición:
 *   base + línea de énfasis del bloque + línea de foco (solo d5) + targets
 *
 * Función pura: se puede probar sin abrir nada.
 */
export function construirPrompt(clave: ClavePrompt, opciones: OpcionesPrompt = {}): string {
  const plantilla = PROMPTS[clave];
  const partes: string[] = [plantilla.base];

  if (plantilla.requiereFoco) {
    if (opciones.foco) {
      partes.push(`My focus for today is: ${AREA_EN_INGLES[opciones.foco]}.`);
    } else {
      // Sin foco decidido no se inventa uno: se dice la verdad.
      partes.push(
        `I have not settled on a focus area yet. Before we start, ask me once which of these I want to work on today, and then commit to it: listening comprehension, lexical retrieval, spontaneous speaking, naturalness, grammar and fluency, pronunciation and prosody, or interaction and turn-taking.`,
      );
    }
  }

  if (opciones.enfasis) {
    partes.push(opciones.enfasis.lineaPrompt);
  }

  const targets = (opciones.targets ?? []).filter((t) => t.trim().length > 0);
  if (targets.length > 0) {
    partes.push(
      `These are things I have been getting wrong or trying to use lately. Work them in naturally where they fit; do not force all of them: ${targets.join('; ')}.`,
    );
  }

  return partes.join('\n\n');
}
